import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { InfoBanner } from '@ui';
import { Spinner } from '@ds';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { getCaseFormDefaultValues } from '@features/components/CaseFormBlocks';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { SearchableListbox } from '@features/components/CaseFormBlocks/components/SearchableListbox';
import { useCaseByIdQuery, useReallocationCandidatesQuery } from '@/queries/case';
import { getUnosId } from '@/utils/helpers';
import { Case, ReallocationCandidate } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { getTriggerContent, renderItem } from './ReallocationListboxHelpers';
import { INFO_MESSAGES_IS_TRIAL_REFERENCE_CASE, INFO_MESSAGES_IS_RELLOCATION_CASE } from './utils';

export const Reallocation = ({
  disabled,
  override,
  initialReallocationCaseId,
}: {
  disabled?: boolean;
  override: boolean;
  initialReallocationCaseId?: Uuid | null;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedReferenceCaseId, setAppliedReferenceCaseId] = useState(initialReallocationCaseId);
  const { setValue, control, reset } = useFormContext<CaseFormType>();
  const [
    donorId,
    organType,
    donorType,
    reAllocationCaseId,
    reAllocationCase,
    serviceRegionId,
    originalFiles,
    originalTxCenterId,
    originalTxCenterName,
    originalTxCenterLocation,
    originalTxCenterTrialPrograms,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    coordinatorId,
    receivingSurgeonId,
    laterality,
  ] = useWatch({
    control,
    name: [
      'donorId',
      'organType',
      'donorType',
      'reAllocationCaseId',
      'reAllocationCase',
      'serviceRegionId',
      'files',
      'txCenterId',
      'txCenterName',
      'txCenterCustomLocation',
      'txCenterTrialPrograms',
      'recipientHospitalDate',
      'recipientHospitalTime',
      'recipientHospitalTimeZone',
      'coordinatorId',
      'receivingSurgeonId',
      'laterality',
    ],
  });

  const referenceCaseUnosId = getUnosId(donorId, organType, undefined, undefined, undefined, laterality);

  const { data: casesAvailableForReallocation = [] } = useReallocationCandidatesQuery({
    organType: override ? undefined : organType,
    donorType: override ? undefined : donorType,
    initialReallocationCaseId: initialReallocationCaseId || undefined,
    serviceRegionId,
  });

  const { data: referenceCase, isFetching: isFetchingReferenceCase } = useCaseByIdQuery(
    reAllocationCaseId!,
    !!reAllocationCaseId,
  );

  const listboxItems = useMemo((): ReallocationCandidate[] => {
    const contains = (str: string) => str.toLowerCase().includes(searchQuery.toLowerCase());
    return casesAvailableForReallocation.filter((c) => contains(c.caseName));
  }, [casesAvailableForReallocation, searchQuery]);

  useEffect(() => {
    if (!reAllocationCaseId && appliedReferenceCaseId) {
      setAppliedReferenceCaseId(undefined);
    }
  }, [reAllocationCaseId, appliedReferenceCaseId]);

  useEffect(() => {
    if (referenceCase && appliedReferenceCaseId === referenceCase.caseId) {
      setValue('reAllocationCaseTrialProgram', referenceCase.trialProgram);
    }
  }, [appliedReferenceCaseId, referenceCase, setValue]);

  useEffect(() => {
    if (override && referenceCase && appliedReferenceCaseId !== referenceCase.caseId) {
      const referenceCaseFormValues = getCaseFormDefaultValues(referenceCase as Case);

      setAppliedReferenceCaseId(referenceCase.caseId);

      reset({
        ...referenceCaseFormValues,
        reAllocationCase: true,
        reAllocationCaseId: referenceCase.caseId,
        txCenterId: originalTxCenterId,
        txCenterName: originalTxCenterName,
        txCenterTrialPrograms: originalTxCenterTrialPrograms,
        txCenterCustomLocation: referenceCaseFormValues.isExpeditedCase ? undefined : originalTxCenterLocation,
        files: originalFiles,
        reAllocationCaseTrialProgram: referenceCaseFormValues.trialProgram,
        recipientHospitalDate,
        recipientHospitalTime,
        recipientHospitalTimeZone,
        coordinatorId,
        receivingSurgeonId,
      });
    }
  }, [
    reset,
    override,
    referenceCase,
    appliedReferenceCaseId,
    originalTxCenterId,
    originalTxCenterLocation,
    originalTxCenterName,
    originalTxCenterTrialPrograms,
    originalFiles,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    coordinatorId,
    receivingSurgeonId,
  ]);

  if (!reAllocationCase) return null;
  const isTrialReferenceCase = !!referenceCase?.trialProgram;

  let infoBannerText: string | null = null;
  if (reAllocationCaseId) {
    infoBannerText = isTrialReferenceCase ? INFO_MESSAGES_IS_TRIAL_REFERENCE_CASE : INFO_MESSAGES_IS_RELLOCATION_CASE;
  }

  return (
    <>
      <FormRow title="Reference case" isRequired={reAllocationCase} multiRow>
        <div className="flex gap-2 flex-1">
          <div className="flex-[48.5%] grow-0">
            <Controller
              name="reAllocationCaseId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SearchableListbox
                  disabled={disabled || isFetchingReferenceCase}
                  invalid={false}
                  selectedKeys={value ? [value] : []}
                  onselectionchange={(keys) => {
                    const values = Array.from(keys as Set<string>);
                    onChange(values[0]);
                  }}
                  setSearchQuery={setSearchQuery}
                  getTriggerContent={() =>
                    getTriggerContent(referenceCase as Case, reAllocationCaseId, referenceCaseUnosId)
                  }
                  items={listboxItems}
                  renderItem={(item, handlers) => renderItem(item as ReallocationCandidate, handlers)}
                />
              )}
            />
          </div>
          {isFetchingReferenceCase && <Spinner />}
        </div>
      </FormRow>
      {infoBannerText && <InfoBanner>{infoBannerText}</InfoBanner>}
    </>
  );
};
